<template>
  <div v-if="!isLoading" class="card p-6">
    <div class="flex justify-between items-start">
      <div>
        <router-link :to="{ name: 'ShowProject', params: { customerId: project.customerId, projectId: project.id } }">
          <h3 class="text-lg font-medium">{{ project.name }}</h3>
        </router-link>
        <p class="text-gray-600 mt-1">{{ project.address }}</p>
      </div>
      <ProjectStatusBadge :status="project.status" />
    </div>
    
    <div class="mt-4">
      <p class="text-gray-600">{{ project.description }}</p>
    </div>

    <!-- Progress Section -->
    <div class="mt-4">
      <div class="flex justify-between items-center mb-2">
        <span class="text-sm font-medium text-gray-700">Project Progress</span>
        <span class="text-sm text-gray-500">{{ projectProgress }}%</span>
      </div>
      <BaseProgressBar :progress="projectProgress" variant="primary" />
    </div>

    <!-- Quick Actions -->
    <div class="mt-4 flex gap-2">
      <BaseButton
        v-if="proposal?.id && project.status == PROJECT_STATUS.AWAITING_ACCEPTANCE"
        text="View Proposal"
        icon-class="i-heroicons-document-text"
        variant="secondary" 
        size="sm"
        :to="{ name: 'CustomerViewProposal', params: { id: proposal.id } }"
      />
      <BaseButton
        v-else
        text="View Details"
        icon-class="i-heroicons-eye"
        variant="secondary" 
        size="sm"
        :to="{ name: 'ShowProject', params: { customerId: project.customerId, projectId: project.id } }"
      />
    </div>

    <!-- Recent Updates -->
    <CustomerProjectMessages :project="project" />
  </div>
  <div v-else class="card p-6">
    <div class="animate-pulse">
      <div class="h-4 bg-slate-200 rounded w-3/4 mb-4"></div>
      <div class="h-32 bg-slate-200 rounded mb-4"></div>
      <div class="space-y-3">
        <div class="h-4 bg-slate-200 rounded w-1/2"></div>
        <div class="h-4 bg-slate-200 rounded w-2/3"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType, computed } from 'vue'
import ProjectStatusBadge from '@/components/project/ProjectStatusBadge.vue'
import BaseProgressBar from '@/components/base/BaseProgressBar.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import CustomerProjectMessages from '@/components/project/CustomerProjectMessages.vue'
import { PROJECT_STATUS, type Project } from '@/models/project'
import BudgetService from '@/services/BudgetService'
import { useQuery } from '@tanstack/vue-query'
import type { BudgetItem } from '@/models/budget'
import ProposalService from '@/services/ProposalService'
import { eq, orderByDesc } from '@/utils/queryFilters'

const props = defineProps({
  project: {
    type: Object as PropType<Project>,
    required: true
  }
})

const { data: budget, isLoading: isBudgetLoading } = useQuery({
  queryKey: ['budgets', props.project.id],
  queryFn: async () => {
    const budget = await new BudgetService().find(props.project.id)
    return budget
  }
})

const projectProgress = computed(() => {
  if (!budget.value?.lineItems?.length) return 0
  
  const completedItems = budget.value.lineItems.filter((item: BudgetItem) => 
    item.status === 'completed' || item.status === 'accepted'
  )

  return Math.round((completedItems.length / budget.value.lineItems.length) * 100)
})

const { data: proposal, isLoading: isProposalLoading } = useQuery({
  queryKey: ['proposals', props.project.id],
  queryFn: async () => {
    const proposals = await new ProposalService().where(
      [
        eq('projectId', props.project.id),
        eq('customerId', props.project.customerId)
      ],
      [orderByDesc('createdAt')],
      1
    )

    return proposals[0]
  }
})

const isLoading = computed(() => {
  return isProposalLoading.value || isBudgetLoading.value
})
</script>
